import React from 'react';
import { Link } from 'react-router-dom';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { SEO } from '../components/seo/SEO';
import { JsonLd } from '../components/seo/JsonLd';
import { createBreadcrumbSchema } from '../lib/structured-data';
import {
  Shield, Lock, Server, FileCheck,
  EyeOff, Key, Database, CheckCircle2
} from 'lucide-react';

export function Security() {
  return (
    <div className="min-h-screen flex flex-col bg-[var(--color-csai-bg)]">
      <Header />

      <main className="flex-grow">
        <SEO
          title="Security & Data Controls"
          description="How Situs Canada describes security posture, access controls, and review boundaries for Canadian workflows."
          canonical="https://situsai.ca/security"
          ogImage="https://situsai.ca/logo.svg"
        />
        <JsonLd data={createBreadcrumbSchema('/security')} />

        <section className="py-20 sm:py-28 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[var(--color-csai-surface)] text-[var(--color-csai-primary)] text-sm font-medium mb-8">
            <Shield className="w-4 h-4" /> Privacy-aware operating posture
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 text-[var(--color-csai-accent)] tracking-tight">
            Security is part of the workflow, not an afterthought.
          </h1>
          <p className="text-xl text-[var(--color-csai-muted)] max-w-3xl mx-auto mb-10">
            Orca is designed for CPA practices that need controlled access, clear outputs, and auditable review steps.
          </p>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Card className="p-10 border border-[var(--color-csai-surface)] bg-white hover:shadow-md transition-shadow">
              <div className="w-14 h-14 bg-[var(--color-csai-surface)] text-[var(--color-csai-primary)] rounded-xl flex items-center justify-center mb-6">
                <Server className="w-7 h-7" />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-[var(--color-csai-accent)]">Privacy-aware infrastructure</h3>
              <p className="text-[var(--color-csai-muted)] leading-relaxed">
                Residency, hosting, and processing controls are designed to align with Canadian expectations for sensitive files.
              </p>
            </Card>

            <Card className="p-10 border border-[var(--color-csai-surface)] bg-white hover:shadow-md transition-shadow">
              <div className="w-14 h-14 bg-[var(--color-csai-surface)] text-[var(--color-csai-primary)] rounded-xl flex items-center justify-center mb-6">
                <EyeOff className="w-7 h-7" />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-[var(--color-csai-accent)]">No model training on firm data</h3>
              <p className="text-[var(--color-csai-muted)] leading-relaxed">
                Customer documents are not used to train the system. They remain available for retrieval and drafting within your workspace model.
              </p>
            </Card>

            <Card className="p-10 border border-[var(--color-csai-surface)] bg-white hover:shadow-md transition-shadow">
              <div className="w-14 h-14 bg-[var(--color-csai-surface)] text-[var(--color-csai-primary)] rounded-xl flex items-center justify-center mb-6">
                <Lock className="w-7 h-7" />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-[var(--color-csai-accent)]">Transport and encryption
              </h3>
              <p className="text-[var(--color-csai-muted)] leading-relaxed">
                Standard TLS and at-rest encryption protect files in transit and at storage.
              </p>
            </Card>

            <Card className="p-10 border border-[var(--color-csai-surface)] bg-white hover:shadow-md transition-shadow">
              <div className="w-14 h-14 bg-[var(--color-csai-surface)] text-[var(--color-csai-primary)] rounded-xl flex items-center justify-center mb-6">
                <Database className="w-7 h-7" />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-[var(--color-csai-accent)]">Tenant isolation</h3>
              <p className="text-[var(--color-csai-muted)] leading-relaxed">
                Separate vault controls reduce cross-client bleed-through and keep firm work segmented by context.
              </p>
            </Card>
          </div>
        </section>

        <section className="py-24 bg-[var(--color-csai-primary)] text-white mt-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div>
                <h2 className="text-3xl sm:text-4xl font-bold mb-6">Security posture and limitations</h2>
                <p className="text-lg text-[#a3b8cc] mb-8 leading-relaxed">
                  This page states current controls and where detailed attestations apply by provider.
                </p>
                <ul className="space-y-6">
                  <li className="flex items-start gap-4">
                    <CheckCircle2 className="w-6 h-6 text-emerald-400 shrink-0 mt-1" />
                    <div>
                      <h4 className="text-xl font-bold mb-1">Residency and provider details</h4>
                      <p className="text-[#7a9bb8]">Published in the subprocessors page, with current region and controls.</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-4">
                    <CheckCircle2 className="w-6 h-6 text-emerald-400 shrink-0 mt-1" />
                    <div>
                      <h4 className="text-xl font-bold mb-1">CPA workflow boundaries</h4>
                      <p className="text-[#7a9bb8]">Drafts are for internal review. CPA sign-off is part of the workflow.</p>
                    </div>
                  </li>
                </ul>
              </div>
              <div className="bg-[#0a1f38] p-10 rounded-3xl border border-[#1c4b7a] shadow-2xl relative overflow-hidden">
                <div className="absolute top-0 right-0 w-64 h-64 bg-[var(--color-csai-primary-hover)] rounded-full blur-3xl opacity-20 -mr-20 -mt-20"></div>
                <FileCheck className="w-12 h-12 text-emerald-400 mb-6" />
                <h3 className="text-2xl font-bold mb-4">Operational Controls</h3>
                <p className="text-[#a3b8cc] mb-6 leading-relaxed">
                  Audit-friendly logs, provider certificates, and access controls are documented and reviewed as part of operations.
                </p>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg border border-white/10">
                    <span className="text-sm font-medium">Infrastructure provider certifications</span>
                    <span className="text-xs bg-blue-400/20 text-blue-400 px-2 py-1 rounded font-bold uppercase">Supplier status</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg border border-white/10">
                    <span className="text-sm font-medium">Security review</span>
                    <span className="text-xs bg-blue-400/20 text-blue-400 px-2 py-1 rounded font-bold uppercase">Ongoing</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-24 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-[var(--color-csai-accent)] mb-4">Access controls</h2>
            <p className="text-lg text-[var(--color-csai-muted)] max-w-2xl mx-auto">Who can access what is controlled through your firm controls and authenticated sessions.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-[var(--color-csai-surface)] text-center">
              <EyeOff className="w-10 h-10 text-[var(--color-csai-primary)] mx-auto mb-4" />
              <h3 className="text-xl font-bold mb-3 text-[var(--color-csai-accent)]">Support access policy</h3>
              <p className="text-[var(--color-csai-muted)]">Access follows strict role and authorization rules.
              </p>
            </div>
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-[var(--color-csai-surface)] text-center">
              <Key className="w-10 h-10 text-[var(--color-csai-primary)] mx-auto mb-4" />
              <h3 className="text-xl font-bold mb-3 text-[var(--color-csai-accent)]">Session-based access</h3>
              <p className="text-[var(--color-csai-muted)]">Vaults and files are exposed only through authenticated user sessions.
              </p>
            </div>
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-[var(--color-csai-surface)] text-center">
              <Shield className="w-10 h-10 text-[var(--color-csai-primary)] mx-auto mb-4" />
              <h3 className="text-xl font-bold mb-3 text-[var(--color-csai-accent)]">Log visibility</h3>
              <p className="text-[var(--color-csai-muted)]">Access events are recorded and available for operational review.
              </p>
            </div>
          </div>
        </section>

        <section className="py-20 bg-[#e8e5e1] text-center px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6 text-[var(--color-csai-accent)]">Ready to review the setup?</h2>
            <p className="text-xl text-[var(--color-csai-muted)] mb-10">Review our whitepaper and talk with our team.</p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Link to="/pricing">
                <Button size="lg" className="w-full sm:w-auto text-lg px-8">Get Started</Button>
              </Link>
              <a href="https://calendly.com/rcarloc029/30min" target="_blank" rel="noopener noreferrer">
                <Button size="lg" variant="secondary" className="w-full sm:w-auto text-lg px-8">Talk to Security</Button>
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
